import React, { useEffect, useMemo, useState } from "react";
import { Card, Empty, Field } from "../../dashboard/primitives";
import {
  DEFAULT_LAYOUT,
  PLAYGROUND_CARDS,
  resolveOrder,
  saveAppLayout,
  subscribeAppLayout,
} from "../../lib/appLayout";
import { SkeletonList } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

// ─────────────────────────────────────────────────────────────────────────────
// Admin → Layout
//
// Controls what the mobile app's Playground tab shows, without an App Store
// release. Writes `config/appLayout`; the app is subscribed to that doc, so a
// save lands on every device in seconds.
//
// UX intent: one row per card, everything reversible, nothing hidden behind a
// modal. Order and visibility — the two things you actually change — are on the
// row itself; copy overrides live behind a disclosure so the default state
// stays scannable (progressive disclosure, Miller's law). The preview on the
// right mirrors the app's real grid so you can confirm before saving.
// ─────────────────────────────────────────────────────────────────────────────

const META = {
  fontFamily: "var(--fw-mono)",
  fontSize: 10,
  letterSpacing: 1.4,
  textTransform: "uppercase",
  color: "var(--fw-muted)",
};

const byId = Object.fromEntries(PLAYGROUND_CARDS.map((c) => [c.id, c]));

/** Draft state: one editable row per known card, in render order. */
const toRows = (layout) =>
  resolveOrder(layout).map(({ card, entry }) => ({
    id: card.id,
    visible: entry.visible !== false,
    title: entry.title || "",
    subtitle: entry.subtitle || "",
    color: entry.color || "",
  }));

/** Draft rows → the `playground` map we persist. Only real overrides stored. */
const toLayout = (rows) => {
  const playground = {};
  rows.forEach((row, index) => {
    const entry = { order: index };
    if (!row.visible) entry.visible = false;
    if (row.title.trim()) entry.title = row.title.trim();
    if (row.subtitle.trim()) entry.subtitle = row.subtitle.trim();
    if (row.color.trim()) entry.color = row.color.trim();
    playground[row.id] = entry;
  });
  return { playground };
};

const sameRows = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const LayoutSection = () => {
  const [saved, setSaved] = useState(null);
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const loading = useMinLoading(saved === null);

  useEffect(() => {
    const unsub = subscribeAppLayout(
      (layout) => {
        const next = toRows(layout);
        setSaved(next);
        // Don't stomp an in-progress edit if someone else saves meanwhile.
        setRows((cur) => (cur.length === 0 ? next : cur));
      },
      (e) => setError(e?.message || "Could not read layout config."),
    );
    return unsub;
  }, []);

  const dirty = useMemo(
    () => saved !== null && !sameRows(rows, saved),
    [rows, saved],
  );

  const patch = (id, key, value) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [key]: value } : r)));

  const move = (index, delta) =>
    setRows((rs) => {
      const next = index + delta;
      if (next < 0 || next >= rs.length) return rs;
      const copy = [...rs];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });

  const resetCard = (id) =>
    setRows((rs) =>
      rs.map((r) =>
        r.id === id ? { ...r, title: "", subtitle: "", color: "" } : r,
      ),
    );

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await saveAppLayout(toLayout(rows));
      setFlash("Saved — live on every device.");
      setTimeout(() => setFlash(""), 3200);
    } catch (e) {
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const restoreDefaults = () => setRows(toRows(DEFAULT_LAYOUT));

  const visibleCount = rows.filter((r) => r.visible).length;

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <Card
        title="Playground layout"
        action={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {flash && (
              <span style={{ ...META, color: "#2E7D46" }}>
                {flash}
              </span>
            )}
            <button
              type="button"
              className="fw-btn fw-btn--ghost"
              onClick={restoreDefaults}
              disabled={saving || loading}
            >
              Restore defaults
            </button>
            <button
              type="button"
              className="fw-btn fw-btn--primary"
              onClick={save}
              disabled={!dirty || saving || loading}
            >
              {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </button>
          </div>
        }
      >
        <p
          style={{
            margin: "0 0 14px",
            fontSize: 13,
            lineHeight: 1.6,
            color: "var(--fw-muted)",
            maxWidth: "62ch",
          }}
        >
          Controls the <strong>Playground</strong> tab in the mobile app. Hiding a
          card removes it from the grid <em>and</em> unregisters its screen, so a
          stale link can't reach it. Changes are live within seconds — no app
          release, no review. Leave a copy field blank to use the app's built-in
          value.
        </p>

        {error && (
          <div
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              borderRadius: 8,
              fontSize: 13,
              background: "rgba(196,39,29,0.08)",
              color: "var(--fw-red)",
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <SkeletonList count={5} />
        ) : rows.length === 0 ? (
          <Empty>No playground cards defined.</Empty>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {rows.map((row, index) => (
              <Row
                key={row.id}
                row={row}
                index={index}
                total={rows.length}
                meta={byId[row.id]}
                open={expanded === row.id}
                onToggleOpen={() =>
                  setExpanded((cur) => (cur === row.id ? null : row.id))
                }
                onPatch={(k, v) => patch(row.id, k, v)}
                onMove={(d) => move(index, d)}
                onReset={() => resetCard(row.id)}
              />
            ))}
          </div>
        )}
      </Card>

      <Card title="Preview">
        <div style={{ ...META, marginBottom: 10 }}>
          {visibleCount} of {rows.length} cards visible
        </div>
        <Preview rows={rows} />
      </Card>
    </div>
  );
};

const Row = ({
  row,
  index,
  total,
  meta,
  open,
  onToggleOpen,
  onPatch,
  onMove,
  onReset,
}) => {
  const overridden = !!(row.title || row.subtitle || row.color);
  return (
    <div
      style={{
        border: "1px solid var(--fw-line)",
        borderRadius: 12,
        padding: 12,
        opacity: row.visible ? 1 : 0.55,
        background: "var(--fw-surface)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            flexShrink: 0,
            background: row.color || meta?.color || "#333",
          }}
        />

        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {row.title || meta?.title}
            {overridden && (
              <span className="fw-chip" style={{ marginLeft: 8 }}>
                custom
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>
            {row.subtitle || meta?.subtitle}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            type="button"
            className="fw-btn fw-btn--ghost"
            onClick={() => onMove(-1)}
            disabled={index === 0}
            aria-label={`Move ${meta?.title} up`}
            style={{ padding: "4px 10px" }}
          >
            ↑
          </button>
          <button
            type="button"
            className="fw-btn fw-btn--ghost"
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            aria-label={`Move ${meta?.title} down`}
            style={{ padding: "4px 10px" }}
          >
            ↓
          </button>
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            whiteSpace: "nowrap",
          }}
        >
          <input
            type="checkbox"
            checked={row.visible}
            onChange={(e) => onPatch("visible", e.target.checked)}
          />
          Visible
        </label>

        <button
          type="button"
          className="fw-btn fw-btn--ghost"
          onClick={onToggleOpen}
          aria-expanded={open}
          style={{ padding: "4px 10px" }}
        >
          {open ? "Done" : "Edit copy"}
        </button>
      </div>

      {meta?.blurb && !open && (
        <div style={{ ...META, marginTop: 8 }}>{meta.blurb}</div>
      )}

      {open && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: "1px solid var(--fw-line)",
            display: "grid",
            gap: 12,
          }}
        >
          <div
            className="fw-stack-sm"
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Field label="Title override">
              <input
                className="fw-input"
                value={row.title}
                placeholder={meta?.title}
                onChange={(e) => onPatch("title", e.target.value)}
              />
            </Field>
            <Field label="Subtitle override">
              <input
                className="fw-input"
                value={row.subtitle}
                placeholder={meta?.subtitle}
                onChange={(e) => onPatch("subtitle", e.target.value)}
              />
            </Field>
          </div>

          <div
            className="fw-stack-sm"
            style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 12 }}
          >
            <Field label="Card colour">
              <input
                className="fw-input"
                type="color"
                value={row.color || meta?.color || "#333333"}
                onChange={(e) => onPatch("color", e.target.value)}
                style={{ height: 42, padding: 4 }}
              />
            </Field>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
              <button
                type="button"
                className="fw-btn fw-btn--ghost"
                onClick={onReset}
              >
                Reset to app default
              </button>
              {meta?.themed && !row.color && (
                <span style={{ ...META, textTransform: "none", letterSpacing: 0 }}>
                  Currently follows the app's theme accent (adapts to dark mode).
                  Setting a colour here pins it.
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Mirrors the app's real grid: 2-up, square tiles, renumbered after hiding. */
const Preview = ({ rows }) => {
  const shown = rows.filter((r) => r.visible);
  if (!shown.length) {
    return <Empty>Every card is hidden — the Playground tab will be empty.</Empty>;
  }
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: 12,
        maxWidth: 520,
      }}
    >
      {shown.map((row, i) => {
        const meta = byId[row.id];
        return (
          <div
            key={row.id}
            style={{
              aspectRatio: "1 / 1",
              borderRadius: 14,
              padding: 14,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              background: row.color || meta?.color || "#333",
              color: "#F5F1E9",
            }}
          >
            <span style={{ ...META, color: "rgba(245,241,233,0.78)" }}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3 }}>
                {row.title || meta?.title}
              </div>
              <div
                style={{
                  fontSize: 11,
                  marginTop: 3,
                  color: "rgba(245,241,233,0.78)",
                }}
              >
                {row.subtitle || meta?.subtitle}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LayoutSection;
